<template>
    <MainContent
        title="Plazos impagados"
        :value="filters"
        :meta="plazos.meta || {}"
        :disabled="loading"
        @input="val => Object.assign(filters.value, val)"
        @updated="fetchAllDebounced"
    >
        <template #header>
            <template v-if="!hasLoadedOnce && loading">
                <!-- Skeleton: stats + filters + buttons -->
                <Card customClasses="tw:mb-4">
                    <Skeleton padding="tw:p-0" variant="tw:bg-transparent" customClass="tw:w-full">
                        <template #skeleton>
                            <div class="tw:flex tw:flex-col tw:gap-4 tw:w-full">
                                <!-- 4 StatsCards skeleton -->
                                <div class="tw:grid tw:grid-cols-12 tw:gap-4">
                                    <div v-for="n in 4" :key="'stat-sk-' + n" class="tw:col-span-12 tw:md:col-span-6 tw:lg:col-span-3">
                                        <SkeletonItem height="tw:h-32" customClass="tw:w-full tw:rounded-xl" />
                                    </div>
                                </div>
                                <!-- Desglose skeleton: 7 items -->
                                <SkeletonItem height="tw:h-42" customClass="tw:w-full tw:rounded-xl" />

                                <div class="tw:border-t tw:border-gray-100"></div>

                                <!-- Filters skeleton -->
                                <div class="tw:flex tw:flex-col tw:sm:flex-row tw:justify-between">
                                    <div class="tw:w-f tw:flex tw:sm:flex-row tw:gap-3">
                                        <SkeletonItem height="tw:h-10" customClass="tw:w-full tw:sm:w-40 tw:rounded-lg" />
                                        <SkeletonItem height="tw:h-10" customClass="tw:w-full tw:sm:w-48 tw:rounded-lg" />
                                        <SkeletonItem v-if="context === 'bases'" height="tw:h-10" customClass="tw:w-full tw:sm:w-64 tw:rounded-lg" />
                                        <SkeletonItem height="tw:h-10" customClass="tw:w-full tw:sm:w-40 tw:rounded-lg" />
                                        <SkeletonItem height="tw:h-10" customClass="tw:w-full tw:sm:w-40 tw:rounded-lg" />
                                        <SkeletonItem height="tw:h-10" customClass="tw:w-28 tw:rounded-lg" />
                                    </div>

                                    <div class="tw:flex tw:gap-3 tw:justify-end">
                                        <SkeletonItem height="tw:h-9" customClass="tw:w-28 tw:rounded-lg" />
                                        <SkeletonItem height="tw:h-9" customClass="tw:w-32 tw:rounded-lg" />
                                    </div>
                                </div>

                            </div>
                        </template>
                    </Skeleton>
                </Card>
            </template>

            <template v-else>
                <Card customClasses="tw:mb-4">
                    <div class="tw:flex tw:flex-col tw:gap-4 tw:w-full">
                        <PlazosImpagadosStats
                            :estadisticas="estadisticas"
                            :loading="loading"
                            :context="context"
                        />

                        <div class="tw:border-t tw:border-gray-200"></div>

                        <div class="tw:flex tw:flex-col tw:sm:flex-row tw:sm:flex-wrap tw:items-start tw:sm:items-center tw:justify-between tw:gap-3">
                            <div class="tw:w-full tw:sm:w-auto">
                                <PlazosImpagadosFilters
                                    :filters="filters"
                                    :context="context"
                                    :equipos="equipos"
                                    :update-filter="updateFilter"
                                    @reset="onResetFilters"
                                />
                            </div>
                            <div class="tw:flex tw:gap-3 tw:w-full tw:sm:w-auto">
                                <Button
                                    class="tw:flex-1 tw:sm:flex-none"
                                    @click="showModal('descargar-plazos')"
                                >
                                    Descargar
                                </Button>
                                <Button
                                    v-if="tienenFalloDefinitivo"
                                    class="tw:flex-1 tw:sm:flex-none"
                                    @click="showModal('avisos-plazos')"
                                >
                                    Enviar avisos
                                </Button>
                            </div>
                        </div>
                    </div>
                </Card>
            </template>
        </template>

        <div class="tw:bg-white tw:rounded-xl">
            <BaseTable
                :columns="tableColumns"
                :data="plazos.data || []"
                :skeleton="loading"
                :skeleton-rows="5"
                empty-text="No hay plazos"
                :default-col-width="defaultColWidth"
            >
                <template #cell-estado="{ row }">
                    <div
                        class="tw:h-3 tw:w-3 tw:rounded-full"
                        :class="getEstadoDotClass(row.estado)"
                        :title="getEstadoTitle(row.estado)"
                    ></div>
                </template>

                <template #cell-fullName="{ row }">
                    <span class="tw:truncate">{{ row.fullName }}</span>
                </template>

                <template v-if="!sonAbonos" #cell-equipo="{ row }">
                    <span>{{ row.equipo || '-' }}</span>
                </template>

                <template #cell-email="{ row }">
                    <span>{{ row.email || '-' }}</span>
                </template>

                <template #cell-fecha="{ row }">
                    <span>{{ formatDateOnly(row.fecha) }}</span>
                </template>

                <template #cell-importe="{ row }">
                    <span>{{ formatPrice(row.importe) }}</span>
                </template>

                <template #cell-estado_string="{ row }">
                    <span
                        class="tw:inline-flex tw:items-center tw:px-2 tw:py-0.5 tw:rounded-full tw:text-xs tw:font-medium"
                        :class="getEstadoBadgeClass(row.estado)"
                    >
                        {{ row.estado_string || '-' }}
                    </span>
                </template>

                <template #cell-error_string="{ row }">
                    <span>{{ row.error_string || '-' }}</span>
                </template>

                <template #cell-email_fecha="{ row }">
                    <span>{{ row.email_fecha || '-' }}</span>
                </template>

                <template #cell-numPlazo="{ row }">
                    <span>{{ row.numPlazo || '-' }}</span>
                </template>

                <template #cell-opciones="{ row }">
                    <template v-if="row.estado == 4 && !row.inactivo">
                        <OptionsDropdownComponent
                            :options="[
                                { type: 'action', action: () => showModal('aviso-plazo-' + row.id), label: 'Enviar aviso' }
                            ]"
                        />
                        <EnviarAvisoModal
                            :plazo-id="row.id"
                            :loading="sendingAviso"
                            :son-abonos="sonAbonos"
                            @close="closeModal"
                            @terminado="enviarAviso(row.id)"
                        />
                    </template>
                    <template v-else-if="row.inactivo">
                        <Button disabled>
                            Aviso enviado{{ sonAbonos ? '/Abono anulado' : '' }}
                        </Button>
                    </template>
                </template>
            </BaseTable>
        </div>

        <!-- Skeleton footer (solo primera carga) -->
        <template v-if="!hasLoadedOnce && loading">
            <Card :custom-classes="'tw:flex tw:flex-col tw:items-center tw:justify-between tw:gap-2 tw:pl-4 tw:pr-4 tw:py-2 tw:mt-6'">
                <Skeleton padding="tw:p-0" variant="tw:bg-transparent" customClass="tw:w-full">
                    <template #skeleton>
                        <div class="tw:flex tw:flex-col tw:w-full tw:md:flex-row tw:md:items-center tw:md:justify-between">
                            <div class="tw:flex tw:justify-between tw:items-center tw:gap-3">
                                <SkeletonItem height="tw:h-4" customClass="tw:w-20 tw:rounded tw:md:tw:hidden" />
                                <SkeletonItem height="tw:h-9" customClass="tw:w-18 tw:rounded-lg" />
                            </div>
                            <div class="tw:hidden tw:md:block">
                                <SkeletonItem height="tw:h-4" customClass="tw:w-48 tw:rounded tw:mx-auto" />
                            </div>
                            <div class="tw:flex tw:justify-center tw:items-center tw:gap-1">
                                <SkeletonItem v-for="n in 3" :key="'pag-sk-' + n" height="tw:h-8" customClass="tw:w-8 tw:rounded" />
                            </div>
                        </div>
                    </template>
                </Skeleton>
            </Card>
        </template>

        <DescargarListadoPlazosModal
            :filters="filters"
            :lista_id="lista_id"
            :tipo="sonAbonos ? 'abono' : 'base'"
            :equipos="equipos"
            @close="closeModal"
        />
        <EnviarAvisosModal
            v-if="tienenFalloDefinitivo"
            :son-abonos="sonAbonos"
            :loading="sendingAvisos"
            @close="closeModal"
            @terminado="enviarAvisos"
        />
    </MainContent>
</template>

<script setup>
    import { computed, ref, watch, onMounted, onUnmounted, nextTick } from 'vue'
    import debounce from 'lodash/debounce'
    import store from '@/store'
    import { config } from '@/config.js'
    import { useToast } from '@/composables/common/useToast'
    import { useModal } from '@/composables/common/useModal'
    import { useRoute, useRouter } from '@/composables/common/router/useRouter'

    import MainContent from '@/modules/admin/ui/MainContent.vue'
    import Card from '@/modules/admin/ui/Card.vue'
    import Button from '@/modules/admin/ui/Button.vue'
    import BaseTable from '@/modules/admin/ui/table/BaseTable.vue'
    import OptionsDropdownComponent from '@/modules/admin/ui/OptionsDropdownComponent.vue'
    import { Skeleton, SkeletonItem } from '@/modules/admin/ui/skeleton'
    import PlazosImpagadosStats from '@/modules/admin/plazos/components/PlazosImpagadosStats.vue'
    import PlazosImpagadosFilters from '@/modules/admin/plazos/components/PlazosImpagadosFilters.vue'
    import DescargarListadoPlazosModal from '@/modules/admin/plazos/components/DescargarListadoPlazosModal.vue'
    import EnviarAvisosModal from '@/modules/admin/plazos/components/EnviarAvisosModal.vue'
    import EnviarAvisoModal from '@/modules/admin/plazos/components/EnviarAvisoModal.vue'

    import usePlazosImpagados from '@/composables/admin/plazos/usePlazosImpagados'

    import appFilters from '@/filters'
    import { formatDateOnly } from '@/utils/dateUtils'

    const props = defineProps({
        context: {
            type: String,
            required: true,
            validator: value => ['abonos', 'bases'].includes(value)
        },
        lista_id: {
            type: [Number, String],
            required: true
        },
        defaultColWidth: {
            type: String,
            default: '120px'
        }
    })

    const toast = useToast()
    const modal = useModal()
    const route = useRoute()
    const router = useRouter()

    const isHydrating = ref(false)

    function updateUrlQuery() {
        const f = filters.value
        const query = {}

        if (f.search) query.search = f.search
        if (f.page && f.page > 1) query.page = String(f.page)
        if (f.per_page && f.per_page !== 10) query.per_page = String(f.per_page)
        if (f.estado) query.estado = f.estado
        if (f.fecha_desde) query.fecha_desde = f.fecha_desde
        if (f.fecha_hasta) query.fecha_hasta = f.fecha_hasta

        router.replace({ query }).catch(() => {})
    }

    const updateUrlDebounced = debounce(() => updateUrlQuery(), 500)

    const {
        plazos,
        loading,
        estadisticas,
        equipos,
        filters,
        fetchPlazos,
        fetchEstadisticas,
        fetchEquipos,
        updateFilter
    } = usePlazosImpagados(props.context)

    const hasLoadedOnce = ref(false)
    watch(plazos, val => {
        if (val?.data?.length) hasLoadedOnce.value = true
    })

    const sendingAvisos = ref(false)
    const sendingAviso = ref(false)

    const sonAbonos = computed(() => props.context === 'abonos')

    const tienenFalloDefinitivo = computed(() => {
        if (plazos.value.data && plazos.value.data.length) {
            return plazos.value.data.some(
                plazo => plazo.estado == 4 && !plazo.inactivo
            )
        }
        return false
    })

    const baseColumns = computed(() => {
        const cols = [
            { key: 'estado', label: 'Activo', class: 'tw:w-20', centered: true },
            { key: 'fullName', label: 'Nombre', class: 'tw:w-40' },
            { key: 'email', label: 'Email', class: 'tw:w-64' },
            { key: 'fecha', label: 'Fecha cobro', class: 'tw:w-28' },
            { key: 'importe', label: 'Importe', class: 'tw:w-24' },
            { key: 'estado_string', label: 'Estado', class: 'tw:w-32', centered: true },
            { key: 'error_string', label: 'Tipo error', class: 'tw:w-36' },
            { key: 'email_fecha', label: 'Fecha email', class: 'tw:w-28' },
            { key: 'numPlazo', label: 'Nº Plazo', class: 'tw:w-20', centered: true },
            { key: 'opciones', label: '', class: 'tw:w-32' }
        ]
        if (!sonAbonos.value) {
            cols.splice(2, 0, { key: 'equipo', label: 'Equipo', class: 'tw:w-32' })
        }
        return cols
    })

    const tableColumns = computed(() => baseColumns.value)

    function getEstadoDotClass(estado) {
        const map = {
            0: 'tw:bg-yellow-400',
            1: 'tw:bg-green-500',
            2: 'tw:bg-gray-600',
            3: 'tw:bg-orange-400',
            4: 'tw:bg-red-500',
            5: 'tw:bg-gray-900'
        }
        return map[estado] || 'tw:bg-red-500'
    }

    function getEstadoTitle(estado) {
        const map = {
            0: 'Pendiente',
            1: 'Pagado',
            2: 'Anulado',
            3: 'Fallido',
            4: 'Fallido definitivo',
            5: 'Bloqueado'
        }
        return map[estado] || ''
    }

    function getEstadoBadgeClass(estado) {
        const map = {
            0: 'tw:bg-yellow-100 tw:text-yellow-800',
            1: 'tw:bg-green-100 tw:text-green-800',
            2: 'tw:bg-gray-100 tw:text-gray-800',
            3: 'tw:bg-orange-100 tw:text-orange-800',
            4: 'tw:bg-red-100 tw:text-red-800',
            5: 'tw:bg-gray-200 tw:text-gray-800'
        }
        return map[estado] || 'tw:bg-gray-100 tw:text-gray-800'
    }

    const formatPrice = appFilters.formatear_precio

    function fetchAll() {
        fetchPlazos(props.lista_id)
        fetchEstadisticas(props.lista_id)
    }

    const fetchAllDebounced = debounce(fetchAll, 500)

    function onResetFilters() {
        Object.assign(filters.value, {
            per_page: 10,
            page: 1,
            estado: '',
            search: '',
            equipo: [],
            fecha_desde: '',
            fecha_hasta: ''
        })
        fetchAll()
    }

    function enviarAviso(plazoId) {
        sendingAviso.value = true

        const params = {
            lista_id: props.lista_id,
            plazo_id: plazoId,
            tipo: props.context
        }

        store.dispatch(config.getVuexAlias('back.plazos-impagados.enviar-aviso'), params)
            .then(() => {
                toast.success('Aviso enviado correctamente.')
                closeModal('aviso-plazo-' + plazoId)
                fetchAll()
            })
            .catch(() => {
                toast.error('Ha ocurrido un error.')
            })
            .finally(() => {
                sendingAviso.value = false
            })
    }

    function enviarAvisos() {
        sendingAvisos.value = true

        const params = {
            filters: {},
            lista_id: props.lista_id,
            tipo: props.context
        }

        store.dispatch(config.getVuexAlias('back.plazos-impagados.enviar-avisos'), params)
            .then(() => {
                toast.success('Avisos enviados correctamente.')
                closeModal('avisos-plazos')
                fetchAll()
            })
            .catch(error => {
                console.error('Error enviando avisos:', error)
                toast.error('Error al enviar los avisos')
            })
            .finally(() => {
                sendingAvisos.value = false
            })
    }

    function showModal(name) {
        modal.show(name)
    }

    function closeModal(name) {
        modal.hide(name)
    }

    watch(filters, () => {
        if (isHydrating.value) return
        fetchAllDebounced()
        updateUrlDebounced()
    }, { deep: true })

    onMounted(() => {
        const query = route.query
        const queryKeys = Object.keys(query)
        if (queryKeys.length > 0) {
            isHydrating.value = true
            if (query.search) filters.value.search = query.search
            if (query.page) filters.value.page = parseInt(query.page, 10)
            if (query.per_page) filters.value.per_page = parseInt(query.per_page, 10)
            if (query.estado) filters.value.estado = query.estado
            if (query.fecha_desde) filters.value.fecha_desde = query.fecha_desde
            if (query.fecha_hasta) filters.value.fecha_hasta = query.fecha_hasta
            nextTick(() => { isHydrating.value = false })
        }

        fetchAll()
        fetchEquipos(props.lista_id)
    })

    onUnmounted(() => {
        fetchAllDebounced.cancel()
        updateUrlDebounced.cancel()
    })
</script>
